import { CompilerInvariantError } from "../../../errors";
import { sql, type SqlFragment } from "../../sql-fragment";
import { type LogicalPlan } from "../plan";
import { inspectRecursiveProjectPlan } from "./plan-inspector";

export type RecursiveQueryEmitterInput = Readonly<{
  depthFilter: SqlFragment;
  limitOffset?: SqlFragment;
  logicalPlan: LogicalPlan;
  orderBy?: SqlFragment;
  /**
   * Non-recursive CTE definitions the recursive CTE reads, in dependency order.
   * They join the same `WITH RECURSIVE` list — both dialects accept a mix.
   */
  precedingCtes?: readonly SqlFragment[];
  projection: SqlFragment;
  recursiveCte: SqlFragment;
  resultAlias?: string;
}>;

function assertRecursiveEmitterClauseAlignment(
  logicalPlan: LogicalPlan,
  input: RecursiveQueryEmitterInput,
): void {
  const planShape = inspectRecursiveProjectPlan(logicalPlan);
  if (planShape.hasSort && input.orderBy === undefined) {
    throw new CompilerInvariantError(
      "Recursive SQL emitter expected ORDER BY clause for plan containing a sort node",
      { component: "recursive-emitter" },
    );
  }
  if (!planShape.hasSort && input.orderBy !== undefined) {
    throw new CompilerInvariantError(
      "Recursive SQL emitter received ORDER BY clause for a plan without sort nodes",
      { component: "recursive-emitter" },
    );
  }
  if (planShape.hasLimitOffset && input.limitOffset === undefined) {
    throw new CompilerInvariantError(
      "Recursive SQL emitter expected LIMIT/OFFSET clause for plan containing a limit_offset node",
      { component: "recursive-emitter" },
    );
  }
  if (!planShape.hasLimitOffset && input.limitOffset !== undefined) {
    throw new CompilerInvariantError(
      "Recursive SQL emitter received LIMIT/OFFSET clause for a plan without limit_offset nodes",
      { component: "recursive-emitter" },
    );
  }
}

export function emitRecursiveQuerySql(
  input: RecursiveQueryEmitterInput,
): SqlFragment {
  assertRecursiveEmitterClauseAlignment(input.logicalPlan, input);

  const cteList = sql.join(
    [...(input.precedingCtes ?? []), input.recursiveCte],
    sql`, `,
  );
  const parts: SqlFragment[] = [
    sql`WITH RECURSIVE`,
    cteList,
    sql`SELECT ${input.projection}`,
    input.resultAlias === undefined ?
      sql`FROM recursive_cte`
    : sql`FROM recursive_cte AS ${sql.identifier(input.resultAlias)}`,
    input.depthFilter,
  ];

  if (input.orderBy !== undefined) {
    parts.push(input.orderBy);
  }
  if (input.limitOffset !== undefined) {
    parts.push(input.limitOffset);
  }

  return sql.join(parts, sql` `);
}
